import { useState, type ReactNode } from "react";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/react/shallow";
import {
  CustomMultiSelectCombobox,
  type CustomMultiSelectComboboxOption,
} from "../../../../components/customCombobox/CustomMultiSelectCombobox";
import {
  Badge,
  ButtonSpinner,
  H6,
  Icon,
  InputCheckbox,
  InputText,
  ProfilePicture,
} from "@sellar-io/components";
import { pluralize } from "@sellar-io/common";
import { useDiscoverySupplierList } from "../../../../hooks/api/v2/discovery/useDiscoverySupplierList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { FilterCard } from "./FilterCard";
import { useCanShowDeliveredBySellarOptionV2 } from "../lib/useCanShowDeliveredBySellarOptionV2";
import { FilterDrawerHeader } from "../components/DiscoveryMobileFilterDrawer";
import { useCustomMultiSelectCombobox } from "~/components/customCombobox/useCustomMultiSelectCombobox";

export const FilterSupplierCard = ({
  onCloseFilterPage,
}: {
  onCloseFilterPage?: () => void;
}) => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();

  return canShowDeliveredBySellarOptionV2 ? (
    <FilterSupplierCardV2 onCloseFilterPage={onCloseFilterPage} />
  ) : (
    <FilterSupplierCardV1 />
  );
};

const FilterSupplierCardV1 = () => {
  const {
    selectedSuppliers,
    isDirectDelivery,
    isDeliveredBySellar,
    isSellarCreditEligibleOnly,
    addSelectedSupplier,
    removeSelectedSupplier,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      selectedSuppliers: state.selectedSuppliers,
      isDirectDelivery: state.isDirectDelivery,
      isDeliveredBySellar: state.isDeliveredBySellar,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      addSelectedSupplier: state.addSelectedSupplier,
      removeSelectedSupplier: state.removeSelectedSupplier,
    })),
  );
  const [supplierSearch, setSupplierSearch] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [debouncedSupplierSearch] = useDebounce(supplierSearch, 300);
  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";

  const supplierListQuery = useDiscoverySupplierList(
    {
      limit: 10,
      search: debouncedSupplierSearch,
      query: [
        ...(isDirectDelivery
          ? ([
              {
                type: "directDelivery",
                modifier: "EQUALS",
                value: isDirectDelivery,
              },
            ] as const)
          : []),
        ...(isDeliveredBySellar
          ? ([
              {
                type: "deliveredBySellar",
                modifier: "EQUALS",
                value: isDeliveredBySellar,
              },
            ] as const)
          : []),
        ...(isSellarCreditEligibleOnly
          ? ([
              {
                type: "sellarCreditEligibleOnly",
                modifier: "EQUALS",
                value: isSellarCreditEligibleOnly,
              },
            ] as const)
          : []),
      ],
      unAuthorisedUserPostcode,
      retailerPostcode: buyerPostcode,
    },
    {
      enabled: debouncedSupplierSearch.length > 1,
    },
  );

  const suppliers =
    supplierListQuery.data?.pages
      .flat()
      .filter(
        (supplier) =>
          !selectedSuppliers.some(
            (selectedSupplier) => selectedSupplier.id === supplier.id,
          ),
      ) ?? [];
  const showSupplierDropdown =
    inputFocused &&
    debouncedSupplierSearch.length > 1 &&
    (supplierListQuery.isFetching || suppliers.length > 0);

  return (
    <FilterCard className={"flex-col gap-3"}>
      <H6>Supplier</H6>
      <div className="relative">
        <div className="flex h-10 items-center rounded-md border border-solid border-zinc-300 bg-white px-3 focus-within:border-zinc-900">
          <input
            className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-500"
            placeholder="Search suppliers..."
            value={supplierSearch}
            onBlur={() => window.setTimeout(() => setInputFocused(false), 150)}
            onChange={(e) => setSupplierSearch(e.target.value)}
            onFocus={() => setInputFocused(true)}
          />
          <Icon icon={"MAGNIFY"} />
        </div>
        {showSupplierDropdown && (
          <div className="absolute top-[calc(100%+4px)] right-0 left-0 z-20 flex max-h-64 flex-col overflow-auto rounded-md border border-solid border-zinc-200 bg-white shadow-lg">
            {supplierListQuery.isFetching && (
              <div className="flex items-center justify-center px-3 py-4">
                <ButtonSpinner className="text-zinc-500" />
              </div>
            )}
            {suppliers.map((supplier) => (
              <button
                className="flex items-center gap-2 border-b border-solid border-zinc-100 px-3 py-2 text-left last:border-b-0 hover:bg-zinc-50"
                key={supplier.id}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  addSelectedSupplier({
                    id: supplier.id,
                    name: supplier.Business.name,
                  });
                  setSupplierSearch("");
                  setInputFocused(false);
                }}
              >
                <ProfilePicture
                  size="small"
                  logoUrl={supplier.Business.logoUrl}
                  fallback="supplier-badge"
                />
                <span className="text-sm font-semibold">
                  {supplier.Business.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!!selectedSuppliers.length && (
        <div className="flex flex-wrap gap-2">
          {selectedSuppliers.map((supplier) => (
            <Badge key={supplier.id}>
              {supplier.name}
              <button
                className="ml-1"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  removeSelectedSupplier(supplier.id);
                }}
              >
                <Icon icon="CLOSE" size={0.75} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </FilterCard>
  );
};

const FilterSupplierCardV2 = ({
  onCloseFilterPage,
}: {
  onCloseFilterPage?: () => void;
}) => {
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const {
    selectedSuppliers,
    isDirectDelivery,
    isDeliveredBySellar,
    isSellarCreditEligibleOnly,
    isMySuppliersOnly,
    setSelectedSuppliers,
    setIsMySuppliersOnly,
    setOrderCutOffAt,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      selectedSuppliers: state.selectedSuppliers,

      isDirectDelivery: state.isDirectDelivery,
      isDeliveredBySellar: state.isDeliveredBySellar,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      isMySuppliersOnly: state.isMySuppliersOnly,
      setIsMySuppliersOnly: state.setIsMySuppliersOnly,
      setSelectedSuppliers: state.setSelectedSuppliers,
      setOrderCutOffAt: state.setOrderCutOffAt,
    })),
  );
  const [supplierSearch, setSupplierSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedSupplierSearch] = useDebounce(supplierSearch, 300);
  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";

  const supplierListQuery = useDiscoverySupplierList(
    {
      limit: 10,
      search: debouncedSupplierSearch,
      query: [
        ...(isDirectDelivery
          ? ([
              {
                type: "directDelivery",
                modifier: "EQUALS",
                value: isDirectDelivery,
              },
            ] as const)
          : []),
        ...(isDeliveredBySellar
          ? ([
              {
                type: "deliveredBySellar",
                modifier: "EQUALS",
                value: isDeliveredBySellar,
              },
            ] as const)
          : []),
        ...(isSellarCreditEligibleOnly
          ? ([
              {
                type: "sellarCreditEligibleOnly",
                modifier: "EQUALS",
                value: isSellarCreditEligibleOnly,
              },
            ] as const)
          : []),
        ...(isMySuppliersOnly
          ? ([
              {
                type: "mySuppliersOnly",
                modifier: "EQUALS",
                value: isMySuppliersOnly,
              },
            ] as const)
          : []),
      ],
      unAuthorisedUserPostcode,
      retailerPostcode: buyerPostcode,
    },
    {
      enabled: isOpen || debouncedSupplierSearch.length > 1,
    },
  );

  const suppliers =
    supplierListQuery.data?.pages.flat().map((supplier) => ({
      id: supplier.id,
      name: supplier.Business.name,
    })) ?? [];

  const renderSupplierFilterSlot = ({
    onChange,
    value,
  }: {
    onChange: (value: CustomMultiSelectComboboxOption[]) => void;
    value: CustomMultiSelectComboboxOption[];
  }) => {
    const isAllMakersFilterEnabled =
      !isMySuppliersOnly &&
      selectedSuppliers.length === 0 &&
      value.length === 0;

    return (
      <ul>
        <li
          role="option"
          aria-selected={isAllMakersFilterEnabled}
          className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
          onClick={() => {
            setIsMySuppliersOnly(false);
            onChange([]);
          }}
        >
          <InputCheckbox
            checked={isAllMakersFilterEnabled}
            readOnly
            className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
          />
          <span className="flex-1">All makers</span>
        </li>
        <li
          role="option"
          aria-selected={isMySuppliersOnly}
          className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
          onClick={() => {
            setIsMySuppliersOnly(!isMySuppliersOnly);
          }}
        >
          <InputCheckbox
            checked={isMySuppliersOnly}
            readOnly
            className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
          />
          <span className="flex-1">My suppliers only</span>
        </li>
      </ul>
    );
  };

  if (isMobile) {
    return (
      <>
        <FilterDrawerHeader title="Supplier" onClick={onCloseFilterPage} />
        <MobileSupplierFilter
          filterSlot={renderSupplierFilterSlot}
          value={selectedSuppliers}
          searchQuery={debouncedSupplierSearch}
          options={suppliers}
          isLoading={supplierListQuery.isFetching}
          singleItemLabel="supplier"
          inputPlaceholder="Search by name..."
          onChange={(options) => {
            setOrderCutOffAt("");
            setSelectedSuppliers(options);
          }}
          onSearch={setSupplierSearch}
        />
      </>
    );
  }

  return (
    <FilterCard className="flex-col gap-3">
      <H6>Supplier</H6>
      <CustomMultiSelectCombobox
        filterSlot={renderSupplierFilterSlot}
        value={selectedSuppliers}
        searchQuery={debouncedSupplierSearch}
        options={suppliers}
        isLoading={supplierListQuery.isFetching}
        singleItemLabel="supplier"
        placeholder="Select supplier(s)..."
        inputPlaceholder="Search by name..."
        onChange={(options) => {
          setOrderCutOffAt("");
          setSelectedSuppliers(options);
        }}
        onSearch={setSupplierSearch}
        onOpenChange={setIsOpen}
      />
    </FilterCard>
  );
};

type MobileSupplierFilterProps = {
  singleItemLabel: string;
  value: CustomMultiSelectComboboxOption[];
  options: CustomMultiSelectComboboxOption[];
  inputPlaceholder: string;
  searchQuery: string;
  isLoading?: boolean;
  filterSlot?: ({
    onChange,
    value,
  }: {
    onChange: (value: CustomMultiSelectComboboxOption[]) => void;
    value: CustomMultiSelectComboboxOption[];
  }) => ReactNode;
  onChange: (value: CustomMultiSelectComboboxOption[]) => void;
  onSearch: (query: string) => void;
};

const MobileSupplierFilter = ({
  singleItemLabel,
  value,
  options,
  inputPlaceholder,
  searchQuery,
  isLoading = false,
  filterSlot,
  onChange,
  onSearch,
}: MobileSupplierFilterProps) => {
  const {
    query,
    selectedOptions,
    unselectedOptions,
    handleSearchChange,
    getToggledValue,
  } = useCustomMultiSelectCombobox({ selection: value, options, onSearch });

  const toggleOption = (option: CustomMultiSelectComboboxOption) => {
    onChange(getToggledValue(option));
  };

  return (
    <>
      <div className="relative border-b border-solid border-zinc-300 p-4">
        <InputText
          type="text"
          placeholder={inputPlaceholder}
          value={query}
          onChange={handleSearchChange}
          className="h-10 w-full rounded-md border border-solid border-zinc-300 bg-white px-3 text-sm outline-none placeholder:text-zinc-500 focus:border-zinc-900"
        />
      </div>
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <ButtonSpinner className="text-zinc-500" />
        </div>
      )}
      {!isLoading && options.length === 0 && query.length > 0 && (
        <div className="px-3 py-4 text-sm text-zinc-500">
          No {pluralize(singleItemLabel, 2)} found
        </div>
      )}
      {!searchQuery && filterSlot && (
        <>
          {filterSlot({ onChange, value })} <hr />
        </>
      )}

      <ul
        role="listbox"
        aria-multiselectable="true"
        className="flex max-h-64 flex-col overflow-auto"
      >
        {selectedOptions.length > 0 && (
          <>
            <div className="px-3 py-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
              Current Selection
            </div>
            {selectedOptions.map((option) => {
              const isSelected = value.some((item) => item.id === option.id);
              return (
                <li
                  key={option.id}
                  role="option"
                  aria-selected={isSelected}
                  className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
                  onClick={() => toggleOption(option)}
                >
                  <InputCheckbox
                    checked={isSelected}
                    readOnly
                    className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
                  />
                  <span className="flex-1">{option.name}</span>
                </li>
              );
            })}
          </>
        )}
        <>
          {searchQuery && unselectedOptions.length > 0 && (
            <div className="px-3 py-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
              Search Results
            </div>
          )}
          {unselectedOptions.map((option) => {
            const isSelected = value.some((item) => item.id === option.id);
            return (
              <li
                key={option.id}
                role="option"
                aria-selected={isSelected}
                className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-zinc-900 hover:bg-zinc-50"
                onClick={() => toggleOption(option)}
              >
                <InputCheckbox
                  checked={isSelected}
                  readOnly
                  className="h-4 w-4 rounded border-zinc-300 text-zinc-900 accent-[#A34FD2] focus:ring-zinc-900"
                />
                <span className="flex-1">{option.name}</span>
              </li>
            );
          })}
        </>
      </ul>
    </>
  );
};
